// ** Modules
import { BlocksRenderer, type BlocksContent } from '@strapi/blocks-react-renderer';

// ** UI
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';

// ** Components
import Link from '@/components/common/Link';

// ** Styles
import { DIN } from '@/styles/globalStyles';

// ** Types
import type { ComponentProps } from 'react';
import type { TypographyProps } from '@mui/material/Typography';
import type { ComponentSharedTextEditor as ComponentSharedTextEditorProps } from '@/types/graphql';

// ** Local Types
type BlocksRendererProps = ComponentProps<typeof BlocksRenderer>;
type BlocksConfig = BlocksRendererProps['blocks'];

export type BlockType = 'paragraph' | 'heading' | 'list' | 'listItem' | 'quote' | 'code' | 'link';

/**
 * Custom props that are not managed in Strapi CMS
 */
type ExtraRendererProps = {
	overrides?: Partial<Record<BlockType, SxProps<Theme>>>;
};

type FullComponentProps = Partial<ComponentSharedTextEditorProps> & ExtraRendererProps;

const variantMapping: Record<string, string> = {
	Normal: 'normal',
	Primary: 'primary'
};

const mapVariant = (type: string, variant?: string | null): TypographyProps['variant'] => {
	const v = variant || 'Normal';
	return `${type}-${variantMapping[v] || 'normal'}` as TypographyProps['variant'];
};

const applyFirstChildStyles = (isFirst: boolean) => {
	return isFirst ? { mt: 0 } : { mt: 3 };
};

const isJsonString = (str: string) => {
	try {
		const result = JSON.parse(str);
		// Strapi Blocks are always an Array at the top level
		return Array.isArray(result);
	} catch {
		return false;
	}
};

const ComponentSharedTextEditor = ({
	variantBody = 'Normal' as ComponentSharedTextEditorProps['variantBody'],
	variantHeading1 = 'Normal' as ComponentSharedTextEditorProps['variantHeading1'],
	variantHeading2 = 'Normal' as ComponentSharedTextEditorProps['variantHeading2'],
	variantHeading3 = 'Normal' as ComponentSharedTextEditorProps['variantHeading3'],
	variantHeading4 = 'Normal' as ComponentSharedTextEditorProps['variantHeading4'],
	variantHeading5 = 'Normal' as ComponentSharedTextEditorProps['variantHeading5'],
	variantHeading6 = 'Normal' as ComponentSharedTextEditorProps['variantHeading6'],
	blockContent,
	overrides = {}
}: FullComponentProps) => {
	const theme = useTheme();
	const textColor = 'text.primary';

	// Track the first child to handle top margins correctly
	let isFirstChild = true;

	// Logic to merge CMS/Base styles with code-level overrides
	const getStyles = (type: BlockType, baseStyles: SxProps<Theme> = []): SxProps<Theme> => {
		const override = overrides[type];
		return [
			...(Array.isArray(baseStyles) ? baseStyles : [baseStyles]),
			...(Array.isArray(override) ? override : override ? [override] : [])
		];
	};

	const blocksConfig: BlocksConfig = {
		paragraph: ({ children }) => {
			const styles = applyFirstChildStyles(isFirstChild);
			isFirstChild = false;

			return (
				<Typography
					variant={mapVariant('body', variantBody)}
					component="p"
					sx={getStyles('paragraph', { color: textColor, ...styles })}
				>
					{children}
				</Typography>
			);
		},
		heading: ({ children, level }) => {
			const styles = applyFirstChildStyles(isFirstChild);
			isFirstChild = false;

			// Map the dynamic level to the specific prop from the CMS
			const variantSettings: Record<number, string | null | undefined> = {
				1: variantHeading1,
				2: variantHeading2,
				3: variantHeading3,
				4: variantHeading4,
				5: variantHeading5,
				6: variantHeading6
			};

			const currentVariant = mapVariant(`h${level}`, variantSettings[level]);

			return (
				<Typography
					variant={currentVariant}
					component={`h${level}` as any}
					sx={getStyles('heading', { color: textColor, ...styles })}
				>
					{children}
				</Typography>
			);
		},
		quote: ({ children }) => (
			<Paper
				elevation={1}
				sx={getStyles('quote', {
					backgroundColor: 'grey.100',
					p: 2,
					my: 2,
					borderLeft: 4,
					borderColor: 'primary.main',
					width: 'max-content'
				})}
			>
				<Typography
					variant="body-md"
					component="blockquote"
					sx={{ margin: 0, fontStyle: 'italic', color: 'text.primary' }}
				>
					{children}
				</Typography>
			</Paper>
		),
		code: ({ plainText }) => (
			<Box
				sx={getStyles('code', {
					width: 'max-content',
					backgroundColor: 'grey.200',
					p: 2,
					borderRadius: 1,
					overflowX: 'auto',
					fontFamily: DIN,
					fontSize: '0.875rem',
					my: 2
				})}
			>
				<pre style={{ margin: 0 }}>
					<code>{plainText}</code>
				</pre>
			</Box>
		),
		link: ({ children, url }) => {
			const linkColor = theme.palette.primary.main;
			return (
				<Link href={url} style={{ color: linkColor, textDecoration: 'underline' }}>
					{children}
				</Link>
			);
		},
		list: ({ format, children }) => {
			const isOrdered = format === 'ordered';
			return (
				<List
					sx={getStyles('list', {
						listStyleType: isOrdered ? 'decimal' : 'disc',
						pl: 4,
						color: textColor,
						'& .MuiListItem-root': { display: 'list-item' }
					})}
					component={isOrdered ? 'ol' : 'ul'}
				>
					{children}
				</List>
			);
		},
		'list-item': ({ children }) => (
			<ListItem sx={{ display: 'list-item', pl: 1.5 }}>
				<Typography variant={mapVariant('body', variantBody)} sx={getStyles('listItem', { color: textColor })}>
					{children}
				</Typography>
			</ListItem>
		)
	};

	if (!blockContent) return null;

	let content: BlocksContent;

	if (typeof blockContent === 'string') {
		if (isJsonString(blockContent)) {
			content = JSON.parse(blockContent) as BlocksContent;
		} else {
			content = [
				{
					type: 'paragraph',
					children: [{ type: 'text', text: blockContent }]
				}
			];
		}
	} else {
		content = (blockContent as any).data || blockContent;
	}

	if (!Array.isArray(content)) {
		return <Typography>{String(blockContent)}</Typography>;
	}

	return <BlocksRenderer content={content} blocks={blocksConfig} />;
};

export default ComponentSharedTextEditor;
